/**
 * Notifications dans l'application (conception 7.5), sans e-mail : le nombre
 * de relances du jour et en retard prefixe le titre de l'onglet, et la
 * premiere ouverture de la journee (heure de Paris) l'annonce par un message,
 * une fois par compte.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { jourParis } from '@leads/shared';
import { useToast } from '../composants/ui';
import { useAuth } from './auth';
import { messageDuJour, titreAvecCompteur, totalRelances } from './prospection';
import { cles, useCompteursProspection } from './requetes';

/** Une cle par compte ; sa valeur, le jour de la derniere annonce : rien ne s'accumule. */
const PREFIXE_CLE = 'leads.relances-annoncees.';

/** Jour de Paris de la derniere annonce faite a ce compte ; `null` si inconnu ou stockage bloque. */
export function jourDerniereAnnonce(utilisateurId: string): string | null {
  try {
    return window.localStorage.getItem(PREFIXE_CLE + utilisateurId);
  } catch {
    return null;
  }
}

export function noterAnnonce(utilisateurId: string, jour: string): void {
  try {
    window.localStorage.setItem(PREFIXE_CLE + utilisateurId, jour);
  } catch {
    /* stockage bloque : le message reviendra au prochain chargement de la page */
  }
}

/**
 * A monter une fois, dans la coquille. Rend le nombre de relances (badge de
 * navigation), relu chaque minute par `useCompteursProspection` : c'est le seul
 * appelant de ce hook, dont `refetchInterval` arme un minuteur par observateur.
 */
export function useNotificationsProspection(): number {
  const { utilisateur } = useAuth();
  const toast = useToast();
  const client = useQueryClient();
  const compteurs = useCompteursProspection();
  const charge = compteurs.data !== undefined;
  const nombre = totalRelances(compteurs.data);
  // Compte et jour de la derniere verification : une par jour de Paris, onglet garde ouvert d'un jour a l'autre
  // compris ; StrictMode rejoue les effets, le message ne double pas.
  const derniereVerification = useRef<string | null>(null);
  // Chiffre du badge a la lecture precedente ; `null` tant qu'aucune n'a abouti.
  const dernierNombre = useRef<number | null>(null);

  useEffect(() => {
    document.title = titreAvecCompteur(document.title, nombre);
  }, [nombre]);

  // Le badge est relu chaque minute, les listes seulement au retour sur l'onglet : quand son chiffre
  // change, elles sont relues avec lui, pour que chiffre et listes concordent et qu'un lead passe en
  // opposition ne reste pas appelable dans une liste ouverte depuis des heures. La premiere lecture
  // ne relit rien : les listes affichees viennent de partir avec elle. Les deux listes seulement, pas
  // la racine `cles.prospection()` : elle relirait aussi les compteurs, qui viennent de changer.
  useEffect(() => {
    if (!charge) return;
    const precedent = dernierNombre.current;
    dernierNombre.current = nombre;
    if (precedent === null || precedent === nombre) return;
    void client.invalidateQueries({ queryKey: cles.aujourdhui() });
    void client.invalidateQueries({ queryKey: [...cles.prospection(), 'relances'] });
  }, [charge, nombre, client]);

  // Deconnexion (la coquille est demontee) : le titre redevient celui de l'application.
  useEffect(
    () => () => {
      document.title = titreAvecCompteur(document.title, 0);
    },
    [],
  );

  // `dataUpdatedAt` : une relecture qui rend les memes chiffres garde leur reference, et passe minuit quand meme.
  const lueLe = compteurs.dataUpdatedAt;
  useEffect(() => {
    const donnees = compteurs.data;
    if (!utilisateur || !donnees) return;
    const jour = jourParis(new Date());
    const verification = `${utilisateur.id}:${jour}`;
    if (derniereVerification.current === verification) return;
    derniereVerification.current = verification;
    if (jourDerniereAnnonce(utilisateur.id) === jour) return;
    // La journee est notee meme sans relance : le message est celui de la premiere ouverture.
    noterAnnonce(utilisateur.id, jour);
    if (totalRelances(donnees) > 0) toast.info(messageDuJour(donnees));
  }, [utilisateur, compteurs.data, lueLe, toast]);

  return nombre;
}
